import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Loader2, Upload, CheckCircle2, XCircle, Lock } from 'lucide-react';

const STEPS = ['Source & Upload', 'Map', 'Dry Run', 'Reconcile', 'Cut Over'];
const ANSWER_KEYS = [
  { key: 'trial_balance', label: 'Trial Balance (at cut-over)' },
  { key: 'balance_sheet_by_year', label: 'Balance Sheet per fiscal year' },
  { key: 'pnl_by_year', label: 'Profit & Loss per fiscal year' },
  { key: 'customer_balances', label: 'Customer balances' },
  { key: 'vendor_balances', label: 'Vendor balances' },
  { key: 'counts_by_type', label: 'Counts by transaction type' },
];

export default function ImportWizard({ base, isDark = true }) {
  const [wiz, setWiz] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [historyFrom, setHistoryFrom] = useState('');
  const [cutoverDate, setCutoverDate] = useState('');
  const [asOf, setAsOf] = useState('');
  const [importTaxIds, setImportTaxIds] = useState(false);
  const [source, setSource] = useState('desktop');
  const jRef = useRef(null); const dRef = useRef(null);
  const akRefs = useRef({});
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const panel = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';

  const load = async () => { try { const r = await axios.get(`${base}/import-wizard`); setWiz(r.data); } catch { /* none */ } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);

  const call = async (name, fn) => {
    setBusy(name); setError('');
    try { const r = await fn(); if (r && r.data) setWiz(r.data); await load(); }
    catch (e) { setError(e?.response?.data?.detail || `${name} failed`); }
    finally { setBusy(''); }
  };
  const readText = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsText(f); });

  const start = () => call('start', () => axios.post(`${base}/import-wizard/start`));
  const upload = () => call('upload', async () => {
    const j = jRef.current?.files?.[0]; const d = dRef.current?.files?.[0];
    if (source === 'online') throw { response: { data: { detail: 'online accounting is coming — awaiting a sample export.' } } };
    if (!j || !d) throw { response: { data: { detail: 'Choose both CSV files' } } };
    const fd = new FormData(); fd.append('source', source); fd.append('journal', j); fd.append('detail', d);
    return axios.post(`${base}/import-wizard/${wiz.wid}/upload`, fd);
  });
  const doMap = () => call('map', () => axios.post(`${base}/import-wizard/${wiz.wid}/map`, { import_tax_ids: importTaxIds }));
  const dryRun = () => call('dry-run', () => axios.post(`${base}/import-wizard/${wiz.wid}/dry-run`, { history_from: historyFrom || null }));
  const commit = () => call('commit', () => axios.post(`${base}/import-wizard/${wiz.wid}/commit`));
  const reconcile = () => call('reconcile', async () => {
    const uploads = {};
    for (const ak of ANSWER_KEYS) { const f = akRefs.current[ak.key]?.files?.[0]; if (f) uploads[ak.key] = await readText(f); }
    return axios.post(`${base}/import-wizard/${wiz.wid}/reconcile`, { uploads, as_of: asOf || null });
  });
  const signoff = (key) => call('signoff', () => axios.post(`${base}/import-wizard/${wiz.wid}/signoff`, { check_key: key, note: 'Reviewed and accepted' }));
  const cutover = () => call('cutover', () => axios.post(`${base}/import-wizard/${wiz.wid}/cutover`, { cutover_date: cutoverDate }));

  const status = wiz?.status;
  const stepIx = { draft: 0, uploaded: 0, mapped: 1, dry_run: 2, imported: 3, reconciled: 3, live: 4 }[status] ?? 0;
  const checks = wiz?.reconcile?.checks || {};
  const signoffs = wiz?.signoffs || {};

  return (
    <div className="space-y-4" data-testid="import-wizard">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {STEPS.map((s, i) => (
            <span key={s} data-testid={`wizard-step-${i}`}
              className={`text-xs px-3 py-1 rounded-full border ${i <= stepIx ? 'bg-emerald-600/20 border-emerald-600 text-emerald-400' : `${panel} ${sub}`}`}>
              {i + 1}. {s}
            </span>
          ))}
        </div>
        {!wiz && <Button data-testid="wizard-start-btn" onClick={start} disabled={busy}>Start Import</Button>}
      </div>

      {status && status !== 'live' && (
        <div className="text-amber-400 text-sm flex items-center gap-2" data-testid="wizard-banner">
          <Lock className="h-4 w-4" /> Imported — not live until you cut over.
        </div>
      )}
      {status === 'live' && (
        <div className="text-emerald-400 text-sm flex items-center gap-2" data-testid="wizard-live-banner">
          <CheckCircle2 className="h-4 w-4" /> Live — history locked at closing date {wiz.closing_date}.
        </div>
      )}
      {error && <div className="text-red-500 text-sm" data-testid="wizard-error">{error}</div>}

      {wiz && (
        <Card className={panel}>
          <CardHeader><CardTitle className="text-base">1 · Source & Upload</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex gap-4">
              <label className="flex items-center gap-2"><input type="radio" data-testid="src-desktop" checked={source === 'desktop'} onChange={() => setSource('desktop')} /> desktop accounting</label>
              <label className={`flex items-center gap-2 ${sub}`}><input type="radio" data-testid="src-online" checked={source === 'online'} onChange={() => setSource('online')} /> online accounting (coming — awaiting sample export)</label>
            </div>
            <div className="flex gap-3 items-center flex-wrap">
              <input ref={jRef} type="file" accept=".csv" data-testid="wizard-journal-input" className={sub} />
              <input ref={dRef} type="file" accept=".csv" data-testid="wizard-detail-input" className={sub} />
              <Button data-testid="wizard-upload-btn" onClick={upload} disabled={busy}><Upload className="h-4 w-4 mr-1" />Upload</Button>
            </div>
            {wiz.files?.journal && <div className={sub}>journal sha {wiz.files.journal.sha256.slice(0, 10)}… · detail sha {wiz.files.detail.sha256.slice(0, 10)}…</div>}
          </CardContent>
        </Card>
      )}

      {wiz && wiz.files?.journal && (
        <Card className={panel}>
          <CardHeader><CardTitle className="text-base">2 · Map</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="wizard-taxids" checked={importTaxIds} onChange={(e) => setImportTaxIds(e.target.checked)} /> Import tax IDs (off by default)</label>
            <Button data-testid="wizard-map-btn" onClick={doMap} disabled={busy}>Analyse & Confirm Map</Button>
            {wiz.map && (
              <div className={sub} data-testid="wizard-map-summary">
                Accounts resolved {wiz.map.accounts_resolved} · unresolved {wiz.map.accounts_unresolved?.length || 0} ·
                names known {wiz.map.names_known} · unknown {wiz.map.names_unknown?.length || 0} ·
                system accounts matched by type {wiz.map.system_accounts?.length || 0}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {wiz && wiz.map && (
        <Card className={panel}>
          <CardHeader><CardTitle className="text-base">3 · Dry Run → Commit</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2">History from (optional):
              <Input data-testid="wizard-history-from" value={historyFrom} onChange={(e) => setHistoryFrom(e.target.value)} placeholder="YYYY-MM-DD" className="w-40" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="wizard-dryrun-btn" onClick={dryRun} disabled={busy}>Dry Run</Button>
              <Button data-testid="wizard-commit-btn" onClick={commit} disabled={busy || !wiz.dry_run}>Commit (full replace)</Button>
            </div>
            {wiz.dry_run && <div className={sub} data-testid="wizard-dryrun-summary">Dry run: {wiz.dry_run.posted} create · {wiz.dry_run.skipped_dup} skip · {wiz.dry_run.needs_review?.length || 0} needs review · {wiz.dry_run.opening_rolled || 0} rolled into opening GJ</div>}
            {wiz.commit_summary && <div className="text-emerald-400" data-testid="wizard-commit-summary">Committed: {wiz.commit_summary.posted} posted.</div>}
          </CardContent>
        </Card>
      )}

      {wiz && wiz.committed && (
        <Card className={panel}>
          <CardHeader><CardTitle className="text-base">4 · Reconcile</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2">Reconcile as of:
              <Input data-testid="wizard-asof" value={asOf} onChange={(e) => setAsOf(e.target.value)} placeholder="YYYY-MM-DD" className="w-40" />
            </div>
            {ANSWER_KEYS.map((ak) => (
              <div key={ak.key} className="flex items-center gap-2">
                <span className="w-64">{ak.label}</span>
                <input type="file" accept=".csv" data-testid={`ak-${ak.key}`} ref={(el) => { akRefs.current[ak.key] = el; }} className={sub} />
              </div>
            ))}
            <Button data-testid="wizard-reconcile-btn" onClick={reconcile} disabled={busy}>Run Checks</Button>
            {Object.keys(checks).length > 0 && (
              <table className="w-full text-xs mt-2" data-testid="wizard-checks">
                <tbody>
                  {Object.entries(checks).map(([k, c]) => (
                    <tr key={k}>
                      <td className="py-1">{c.pass ? <CheckCircle2 className="h-4 w-4 text-emerald-500 inline" /> : <XCircle className="h-4 w-4 text-red-500 inline" />} {k}</td>
                      <td className={sub}>{c.diff_count} diffs / {c.checked} checked</td>
                      <td>{!c.pass && !signoffs[k] && <Button size="sm" variant="outline" data-testid={`signoff-${k}`} onClick={() => signoff(k)}>Sign off</Button>}{signoffs[k] && <span className="text-amber-400">signed off</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {wiz && wiz.committed && status !== 'live' && (
        <Card className={panel}>
          <CardHeader><CardTitle className="text-base">5 · Cut Over</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2">Cut-over date:
              <Input data-testid="wizard-cutover-date" value={cutoverDate} onChange={(e) => setCutoverDate(e.target.value)} placeholder="YYYY-MM-DD" className="w-40" />
            </div>
            <div className={sub}>Enabled only when every check passes or is signed off. Closing date = the day before cut-over.</div>
            <Button data-testid="wizard-cutover-btn" onClick={cutover} disabled={busy || !cutoverDate}>Cut Over</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
